import React, { useEffect, useRef } from 'react';
import { Button } from 'antd';
import { ArrowRightOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import './OpenClawHero.css';

/**
 * OpenClawHero — §1 of /openclaw.
 *
 * Mirrors LinearHero structure: co-brand lockup, vision-led headline,
 * subtitle, dual CTA, trust microcopy. OpenClaw-red atmospheric gradient.
 */
const OpenClawHero = ({ ctaLabel, onGetApiKey, onSeeHowItWorks }) => {
  const rootRef = useRef(null);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const el = rootRef.current;
    if (el) el.classList.add('openclaw-hero--mounted');
  }, []);

  // CellCog mark — theme-adaptive, same naming convention as on /linear.
  //   cellcog-mark-dark.png  = vibrant teal (reads on LIGHT backgrounds)
  //   cellcog-mark-light.png = pale teal (reads on DARK backgrounds)
  const cellcogLogoSrc = isDarkMode
    ? '/assets/linear/cellcog-mark-light.png'
    : '/assets/linear/cellcog-mark-dark.png';

  return (
    <section className="openclaw-hero" ref={rootRef}>
      <div className="openclaw-hero__gradient" aria-hidden="true" />
      <div className="openclaw-hero__inner">
        {/* Co-brand lockup: OpenClaw mascot × CellCog */}
        <div className="openclaw-hero__lockup" aria-label="OpenClaw and CellCog">
          <img
            src="/assets/icons/agents/openclaw.svg"
            alt="OpenClaw"
            className="openclaw-hero__lockup-openclaw"
          />
          <span className="openclaw-hero__lockup-x" aria-hidden="true">
            ×
          </span>
          {/* TWO-FACE RULE (adaptive shell): shell pins dark, so the JS pick
              is the dark-face artwork there; the light twin below shows only
              on the adaptive light face. Logged-in behavior unchanged. */}
          <img
            src={cellcogLogoSrc}
            alt="CellCog"
            className="openclaw-hero__lockup-cellcog mkt-face-dark"
          />
          <img
            src="/assets/linear/cellcog-mark-dark.png"
            alt=""
            className="openclaw-hero__lockup-cellcog mkt-face-light"
          />
        </div>

        <h1 className="openclaw-hero__title display-font">
          Your agents
          <br className="openclaw-hero__title-break" />
          <span className="openclaw-hero__title-accent">just got a super-agent.</span>
        </h1>

        <p className="openclaw-hero__subtitle">
          CellCog is the any-to-any execution layer for OpenClaw agents.
          Delegate research, documents, images, video, audio, and dashboards —
          no framework rewrites, no per-capability plumbing, just one API key.
        </p>

        <div className="openclaw-hero__ctas">
          <Button
            type="primary"
            size="large"
            className="openclaw-hero__cta-primary"
            onClick={onGetApiKey}
            data-msc-suppress
          >
            {ctaLabel}
            <ArrowRightOutlined />
          </Button>
          <Button
            size="large"
            className="openclaw-hero__cta-secondary"
            onClick={onSeeHowItWorks}
            icon={<ArrowDownOutlined />}
          >
            See how it works
          </Button>
        </div>

        <p className="openclaw-hero__trust">
          One API key · ClawHub skill or plugin install · Pay only for what you run ·{' '}
          <Link to="/pricing">View pricing →</Link>
        </p>
      </div>
    </section>
  );
};

export default OpenClawHero;
